import { useState, useRef, useMemo } from "react";
import { X, Tag as TagIcon } from "@phosphor-icons/react";

// Creatable, auto-suggesting tag chip input. value/onChange work with a string[] array.
export default function TagInput({ value = [], onChange, suggestions = [], placeholder = "Add a tag…", testid = "tag-input" }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const add = (raw) => {
    const t = (raw || "").trim();
    if (!t) return;
    if (!value.some((v) => v.toLowerCase() === t.toLowerCase())) onChange([...value, t]);
    setText("");
    setOpen(false);
  };
  const remove = (t) => onChange(value.filter((v) => v !== t));

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    const selected = new Set(value.map((v) => v.toLowerCase()));
    return suggestions
      .filter((s) => !selected.has(s.toLowerCase()) && (!q || s.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [text, value, suggestions]);

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(text); }
    else if (e.key === "Backspace" && !text && value.length) remove(value[value.length - 1]);
  };

  const exactExists = suggestions.some((s) => s.toLowerCase() === text.trim().toLowerCase())
    || value.some((v) => v.toLowerCase() === text.trim().toLowerCase());

  return (
    <div className="relative" data-testid={testid}>
      <div
        onClick={() => inputRef.current?.focus()}
        className="mt-1 flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1.5 text-sm focus-within:ring-1 focus-within:ring-ring"
      >
        {value.map((t) => (
          <span key={t} data-testid={`tag-chip-${t}`} className="inline-flex items-center gap-1 rounded-full bg-secondary/10 py-0.5 pl-2.5 pr-1 text-xs font-medium text-secondary">
            {t}
            <button type="button" onClick={(e) => { e.stopPropagation(); remove(t); }} data-testid={`tag-remove-${t}`} className="rounded-full p-0.5 hover:bg-secondary/20"><X size={11} weight="bold" /></button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onKeyDown={onKey}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          placeholder={value.length ? "" : placeholder}
          className="min-w-[100px] flex-1 border-0 bg-transparent p-0.5 outline-none placeholder:text-muted-foreground"
          data-testid={`${testid}-field`}
        />
      </div>
      {open && (matches.length > 0 || (text.trim() && !exactExists)) && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-border bg-popover shadow-md" data-testid={`${testid}-menu`}>
          {text.trim() && !exactExists && (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); add(text); }} data-testid={`${testid}-create`}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent">
              <TagIcon size={14} className="text-secondary" /> Create <span className="font-medium">“{text.trim()}”</span>
            </button>
          )}
          {matches.map((s) => (
            <button key={s} type="button" onMouseDown={(e) => { e.preventDefault(); add(s); }} data-testid={`${testid}-opt-${s}`}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent">
              <TagIcon size={14} className="text-muted-foreground" /> {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
